
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Event.css";
import eventImg from "../../../../assets/holidays/giftcard.png";

const QUANTITY_OPTIONS = [1, 2, 3, 4, 5, 10];

const INITIAL_CONTACT = {
  name: "",
  email: "",
  phone: "",
};

const INITIAL_RECEIVER = {
  name: "",
  email: "",
  confirmEmail: "",
  phone: "",
};

export default function EventPage({ event, onBack }) {
  const navigate = useNavigate();

  const [denomination, setDenomination] = useState("");
  const [quantity, setQuantity] = useState("");
  const [delivery, setDelivery] = useState("today");

  const [sameAsSender, setSameAsSender] = useState(false);
  const [sender, setSender] = useState(INITIAL_CONTACT);
  const [receiver, setReceiver] = useState(INITIAL_RECEIVER);

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sameAsSender) return;

    setReceiver((current) => ({
      ...current,
      name: sender.name,
      email: sender.email,
      confirmEmail: sender.email,
      phone: sender.phone,
    }));
  }, [sameAsSender, sender]);

  const payableAmount = useMemo(() => {
    const amount = Number(denomination) || 0;
    const count = Number(quantity) || 0;

    return Number((amount * count).toFixed(2));
  }, [denomination, quantity]);

  if (!event) return null;

  const updateSender = (key) => (eventObject) => {
    setSender((current) => ({
      ...current,
      [key]: eventObject.target.value,
    }));
  };

  const updateReceiver = (key) => (eventObject) => {
    setReceiver((current) => ({
      ...current,
      [key]: eventObject.target.value,
    }));
  };

  const validateForm = () => {
    const amount = Number(denomination);
    const count = Number(quantity);

    if (!amount || amount < 500 || amount > 50000) {
      return "Please enter a gift card denomination between ₹500 and ₹50,000.";
    }

    if (!QUANTITY_OPTIONS.includes(count)) {
      return "Please select a valid gift card quantity.";
    }

    if (!sender.name.trim()) {
      return "Please enter the sender's name.";
    }

    if (!sender.email.trim()) {
      return "Please enter the sender's email address.";
    }

    if (!sender.phone.trim()) {
      return "Please enter the sender's mobile number.";
    }

    if (!receiver.name.trim()) {
      return "Please enter the receiver's name.";
    }

    if (!receiver.email.trim()) {
      return "Please enter the receiver's email address.";
    }

    if (!receiver.confirmEmail.trim()) {
      return "Please re-enter the receiver's email address.";
    }

    if (receiver.email.trim().toLowerCase() !== receiver.confirmEmail.trim().toLowerCase()) {
      return "Receiver email addresses do not match.";
    }

    if (!receiver.phone.trim()) {
      return "Please enter the receiver's mobile number.";
    }

    if (!acceptedTerms) {
      return "Please accept the Terms and Conditions to continue.";
    }

    return "";
  };

  const handleSubmit = (eventObject) => {
    eventObject.preventDefault();

    setError("");

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const giftCardData = {
      event_id: event.id || null,
      event_title: event.title || event.name || "",
      denomination: Number(denomination),
      quantity: Number(quantity),
      delivery_type: delivery,

      sender: {
        name: sender.name.trim(),
        email: sender.email.trim(),
        phone: sender.phone.trim(),
      },

      receiver: {
        name: receiver.name.trim(),
        email: receiver.email.trim(),
        phone: receiver.phone.trim(),
      },

      amount: payableAmount,
    };

    /*
     * Payment is intentionally NOT implemented here.
     *
     * The next backend/payment stage should:
     * 1. Validate denomination and quantity again.
     * 2. Calculate the authoritative amount.
     * 3. Create the gift-card purchase/order.
     * 4. Create the payment order.
     * 5. Verify payment on the server.
     * 6. Issue/store the gift card only after successful payment.
     *
     * For now we pass the validated purchase information forward.
     */

    navigate("/gift-card/confirm", {
      state: {
        giftCard: giftCardData,
      },
    });
  };

  return (
    <div className="event-page">
      <section className="event-page-hero">
        <button
          className="event-hero-back"
          type="button"
          onClick={onBack}
        >
          ← Back
        </button>

        <img
          className="event-hero-main-image"
          src={eventImg}
          alt="Gifting holiday event card"
        />
      </section>

      <form
        className="event-form-card"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="event-form-body">
          <p className="event-selected-name">
            {event.title || event.name}
          </p>

          <h3 className="event-form-title">
            Enter Gift Card Details
          </h3>

          {error && (
            <div className="event-form-error" role="alert">
              {error}
            </div>
          )}

          <div className="event-form-grid two">
            <label>
              <span>Enter Denomination</span>

              <input
                value={denomination}
                onChange={(eventObject) => {
                  setDenomination(eventObject.target.value);
                  setError("");
                }}
                type="number"
                min="500"
                max="50000"
                step="1"
                inputMode="numeric"
                placeholder="Min 500 - 50000"
              />
            </label>

            <label>
              <span>Select Quantity</span>

              <select
                value={quantity}
                onChange={(eventObject) => {
                  setQuantity(eventObject.target.value);
                  setError("");
                }}
              >
                <option value="">
                  --Select Quantity--
                </option>

                {QUANTITY_OPTIONS.map((item) => (
                  <option
                    key={item}
                    value={item}
                  >
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="event-delivery-row">
            <span>Delivery Date</span>

            <div className="event-radio-group">
              <label>
                <input
                  type="radio"
                  name="gift-card-delivery"
                  checked={delivery === "today"}
                  onChange={() => {
                    setDelivery("today");
                    setError("");
                  }}
                />
                TODAY
              </label>

              <label>
                <input
                  type="radio"
                  name="gift-card-delivery"
                  checked={delivery === "later"}
                  onChange={() => {
                    setDelivery("later");
                    setError("");
                  }}
                />
                LATER
              </label>
            </div>
          </div>

          <h3 className="event-form-title">
            Enter Sender and Receiver Details
          </h3>

          <div className="event-form-grid two sender-receiver">
            <div className="event-form-column">
              <span className="event-field-heading">
                Sender details
              </span>

              <input
                value={sender.name}
                onChange={updateSender("name")}
                placeholder="Sender's name*"
                autoComplete="name"
              />

              <input
                value={sender.email}
                onChange={updateSender("email")}
                type="email"
                placeholder="Sender's email address*"
                autoComplete="email"
              />

              <input
                value={sender.phone}
                onChange={updateSender("phone")}
                type="tel"
                inputMode="numeric"
                placeholder="Sender's 10 digits mobile number*"
                autoComplete="tel"
              />

              <small>
                OTP will be sent to this number
              </small>
            </div>

            <div className="event-form-column">
              <div className="event-receiver-heading">
                <span className="event-field-heading">
                  Receiver Details
                </span>

                <label>
                  <input
                    type="checkbox"
                    checked={sameAsSender}
                    onChange={(eventObject) => {
                      setSameAsSender(
                        eventObject.target.checked
                      );
                      setError("");
                    }}
                  />

                  Same as sender details
                </label>
              </div>

              <input
                value={receiver.name}
                onChange={updateReceiver("name")}
                placeholder="Receiver's name*"
                autoComplete="name"
              />

              <input
                value={receiver.email}
                onChange={updateReceiver("email")}
                type="email"
                placeholder="Receiver's email address*"
                autoComplete="email"
              />

              <input
                value={receiver.confirmEmail}
                onChange={updateReceiver("confirmEmail")}
                type="email"
                placeholder="Retype receiver's email address*"
                autoComplete="email"
              />

              <input
                value={receiver.phone}
                onChange={updateReceiver("phone")}
                type="tel"
                inputMode="numeric"
                placeholder="Receiver's 10 digits mobile number*"
                autoComplete="tel"
              />
            </div>
          </div>

          <label className="event-terms">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(eventObject) => {
                setAcceptedTerms(
                  eventObject.target.checked
                );
                setError("");
              }}
            />

            <span>
              I accept the{" "}
              <a
                href="#terms"
                onClick={(eventObject) =>
                  eventObject.preventDefault()
                }
              >
                Terms and Conditions
              </a>
            </span>
          </label>

          <div className="event-pay-row">
            <span>
              Payable Amount{" "}
              <strong>
                ₹{payableAmount.toLocaleString("en-IN")}
              </strong>
            </span>

            <button type="submit">
              Continue
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}